import { Classes } from "@blueprintjs/core";
import { observer } from "mobx-react";
import { getSnapshot } from "mobx-state-tree";
import { Search } from "modules/common/components/form/Search";
import { DefaultSelectedOption, renderSingleOption, SimpleSelect } from "modules/common/services/form/select";
import { PlanrButton } from "modules/common/components/planr/button/Button";
import { LocalLoader } from "modules/common/components/loader/Loader";
import { SavingOverlay } from "modules/common/components/loader/SavingOverlay";
import { CollapseExpandAll } from "modules/common/components/table/CollapseExpandAll";
import { NoData } from "modules/common/components/table/NoData";
import { Zoomer } from "modules/common/components/zoomer/Zoomer";
import { texts } from "modules/common/texts";
import { Peoples } from "modules/orders-manage/details/Peoples";
import { Constants } from "modules/root/models/constants";
import { menuItems } from "modules/root/components/menu/MainMenu";
import { canAny } from "modules/session/auth/access";
import { SessionType } from "modules/session/auth/models/session";
import { WorkloadPlanModes } from "modules/session/settings/models/settings";
import func from "modules/spending/functionalities";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Prompt } from "modules/common/components/routing/Prompt";
import { PlanrTooltip } from "modules/common/components/tooltip/PlanrTooltip";
import { usePlanInteractions, DropPreview, MoveIntent, ResizeIntent } from "../hooks/usePlanInteractions";
import { useRangeSelection } from "../hooks/useRangeSelection";
import { buildPlan } from "../models/plan-builder";
import { buildDiff } from "../models/plan-diff";
import {
    clearRange,
    copyRange,
    copySlots,
    fillAbsence,
    fillRange,
    hasContent,
    moveAbsence,
    moveRange,
    moveSlots,
    pasteRange,
    pasteSlots,
    readRange,
    readSlots,
    resizeBlockLeft,
    resizeBlockRight,
} from "../models/plan-operations";
import { deleteWithShift, insertSlotsWithShift, insertWithShift } from "../models/plan-shift";
import { blockAt, blocksOf } from "../models/plan-queue";
import { alignPeriods, buildGrid, PlanScale, slotRange, slotsOfRange, slotsSpan } from "../models/plan-scale";
import { MONTHS_BUFFER, MONTHS_LIMITS, WorkloadPlanStoreType } from "../models/plan-store";
import {
    CellSelection,
    isAbsence,
    isWork,
    PlanCell,
    PlanColumn,
    PlanProject,
    PlanRow,
    PlanSnapshot,
} from "../models/plan-types";
import { DepartmentRow, DepartmentSection } from "./DepartmentSection";
import { PeriodRangeSlider } from "./PeriodRangeSlider";
import { ContextMenu, ContextMenuAction } from "modules/common/components/context-menu/ContextMenu";
import { PlanDayHeader } from "./PlanDayHeader";
import { PlanHelpDialog } from "./PlanHelpDialog";
import { PlanReconcileDialog } from "./PlanReconcileDialog";
import { PlanSubmitButtons } from "./PlanSubmitButtons";
import { planAbsenceOptions, PlanAbsenceOption } from "./plan-absences";
import { ProjectPicker } from "./ProjectPicker";
import styles from "./WorkloadPlanPage.module.scss";

const menu = menuItems();

const EMPTY_PLAN: PlanSnapshot = { columns: [], periods: [], rows: [], projects: [] };

/** Масштаб сетки */
const SCALE_OPTIONS: SelectItem[] = [
    { id: "day", label: "По дням" },
    { id: "week", label: "По неделям" },
];

/** Глубина показа: свой набор вариантов для каждого масштаба */
const MONTHS_OPTIONS: { [key in PlanScale]: SelectItem[] } = { day: [], week: [] };

(Object.keys(MONTHS_OPTIONS) as PlanScale[]).forEach((scale) => {
    const limits = MONTHS_LIMITS[scale];

    for (let months = limits.min; months <= limits.max; months++) {
        MONTHS_OPTIONS[scale].push({ id: String(months), label: `${months} мес.` });
    }
});

/**
 * Кнопка инструкции. Цвета и состояния берём у типа lightblueish,
 * инлайном задаём только размеры — она должна быть мельче обычной.
 */
const HELP_BUTTON_STYLE: React.CSSProperties = {
    height: "22px",
    minHeight: "22px",
    padding: "0 10px",
    fontSize: "11px",
};

interface PickerState {
    x: number;
    y: number;
    selection: CellSelection;
}

/**
 * Буфер обмена страницы. Хранит содержимое по ячейкам сетки того масштаба,
 * в котором копировали: в недельном ячейка — неделя целиком, и вставляется она
 * тоже неделей, сколько бы рабочих дней в ней ни было.
 */
interface PlanClipboard {
    scale: PlanScale;
    /** Дни, сгруппированные по ячейкам: в дневном масштабе группа — один день */
    groups: PlanCell[][];
    /** Плоский список дней — для вставки со сдвигом и для дневного масштаба */
    days: PlanCell[];
}

interface ContextMenuState {
    x: number;
    y: number;
    selection: CellSelection;
}

interface WorkloadPlanPageProps {
    store: WorkloadPlanStoreType;
    session: SessionType;
    setUserFilter: (value: string) => void;
}

export const WorkloadPlanPage = observer((props: WorkloadPlanPageProps) => {
    const { store, session, setUserFilter } = props;

    const [plan, setPlan] = useState<PlanSnapshot>(EMPTY_PLAN);
    const [rows, setRows] = useState<PlanRow[]>([]);
    /** Последнее сохранённое состояние — от него считается дифф и к нему откатывает «Отмена» */
    const [baseline, setBaseline] = useState<PlanRow[]>([]);
    const [selection, setSelection] = useState<CellSelection | null>(null);
    const [dropPreview, setDropPreview] = useState<DropPreview | null>(null);
    const [picker, setPicker] = useState<PickerState | null>(null);
    const [loaded, setLoaded] = useState(false);
    const [contextMenu, setContextMenu] = useState<ContextMenuState | null>(null);
    /** Слот (день или неделя), выделенный кликом по шапке */
    const [selectedSlot, setSelectedSlot] = useState<number | null>(null);
    const [helpOpen, setHelpOpen] = useState(false);
    const [reconcileOpen, setReconcileOpen] = useState(false);
    /** Буфер обмена страницы: скопированный отрезок */
    const [clipboard, setClipboard] = useState<PlanClipboard | null>(null);
    /** Стек состояний для отмены последнего действия */
    const [history, setHistory] = useState<PlanRow[][]>([]);

    const gridRef = useRef<HTMLDivElement>(null);
    const scrollRef = useRef<HTMLDivElement>(null);

    const canManage = canAny([func.WORKLOAD_EDIT, func.WORKLOAD_EDIT_ALL], session.access);

    // Сводить план с табелем может тот, кто видит табель отдела или всей компании.
    // В остальных режимах сверять нечего: план либо едет сам, либо не трогается вовсе.
    const canReconcile =
        store.planMode === WorkloadPlanModes.timesheetDriven &&
        canAny([func.TIMESHEET_EDIT, func.TIMESHEET_EDIT_DEPARTMENT], session.access);

    const load = useCallback(
        async (year?: number, month?: number) => {
            try {
                await store.loadOrders();

                const data = await store.fetch(year, month);

                if (!data) {
                    return;
                }

                const next = buildPlan(
                    data,
                    store.orders.orders.map((order) => getSnapshot(order)),
                    store.monthsOnPage
                );

                setPlan(next);
                setRows(next.rows);
                setBaseline(next.rows);
                setSelection(null);
                setSelectedSlot(null);
                setPicker(null);
                setHistory([]);

                next.rows.forEach((row) => store.collapser.set(row.department, true));
            } finally {
                // «Нет данных» показываем только после первой завершённой загрузки:
                // словарь проектов грузится до fetch и общим лоадером не покрыт
                setLoaded(true);
            }
        },
        [store]
    );

    useEffect(() => {
        document.title = menu.schedules.nested.workload.label;
        load();
        // грузим один раз на монтирование; смена периода идёт через gotoMonth
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    /**
     * Сверка меняет план на сервере, поэтому после неё страницу перечитываем целиком:
     * несохранённые правки при этом потерялись бы, о чём и спрашиваем заранее.
     */
    const reconcile = useCallback(async () => {
        const done = await store.reconcile();

        setReconcileOpen(false);

        if (done) {
            setLoaded(false);
            await load();
        }
    }, [store, load]);

    const projectsMap = useMemo(() => {
        const map = new Map<string, PlanProject>();
        plan.projects.forEach((project) => map.set(project.id, project));
        return map;
    }, [plan.projects]);

    const diff = useMemo(
        () =>
            rows === baseline
                ? null
                : buildDiff(baseline, rows, plan.columns, plan.projects, {
                      workedOutType: Constants.workedOutHoursType.name,
                      workDayHours: Constants.workDayHours,
                  }),
        [baseline, rows, plan.columns, plan.projects]
    );

    const changedDaysCount = diff ? diff.days.length : 0;

    /**
     * Именно изменившиеся дни, а не «массив стал другим объектом»: перестановка
     * пустых ячеек меняет ссылку на rows, но сохранять там нечего.
     */
    const dirty = changedDaysCount > 0;

    const save = useCallback(async () => {
        if (!diff || !diff.days.length) {
            return;
        }

        const saved = await store.persist(diff.days, diff.oldDays);

        if (saved) {
            setBaseline(rows);
            setHistory([]);
        }
    }, [diff, rows, store]);

    const cancel = useCallback(() => {
        setRows(baseline);
        setSelection(null);
        setPicker(null);
        setHistory([]);
    }, [baseline]);

    const gotoMonth = useCallback(
        (year: number, month: number) => {
            if (dirty && !window.confirm("План изменён, но не сохранён. Перейти к другому периоду и потерять правки?")) {
                return;
            }

            setLoaded(false);
            store.setPeriod(year, month);
            load(year, month);
        },
        [store, load, dirty]
    );

    /**
     * Глубина показа своя для каждого масштаба. Пока запрошенные месяцы уже загружены,
     * меняется только видимая часть сетки — без запроса и без потери правок.
     * Перезагрузка нужна, лишь когда месяцев просят больше, чем пришло с сервера.
     */
    const changeMonthsOnPage = useCallback(
        (months: number) => {
            if (months === store.monthsOnPage) {
                return;
            }

            const reload = months + MONTHS_BUFFER > store.loadedMonths;

            if (
                reload &&
                dirty &&
                !window.confirm("План изменён, но не сохранён. Изменить период показа и потерять правки?")
            ) {
                return;
            }

            store.setMonthsOnPage(months);

            if (reload) {
                setLoaded(false);
                load();
            }
        },
        [store, load, dirty]
    );

    /** Масштаб переключается мгновенно: данные загружены под оба режима сразу */
    const changeScale = useCallback(
        (scale: PlanScale) => {
            if (scale === store.scale) {
                return;
            }

            store.setScale(scale);
            setSelection(null);
            setSelectedSlot(null);
            setPicker(null);
        },
        [store]
    );

    /**
     * Видно только первые месяцы; остальные — буфер, куда уезжают вытолкнутые работы.
     * Считаем по индексу месяца, а не по флагу hidden: тогда глубину показа
     * можно менять прямо на странице, не пересобирая план и не теряя правки.
     */
    const visibleColumns = useMemo(
        () => plan.columns.filter((column) => column.periodIndex < store.monthsOnPage),
        [plan.columns, store.monthsOnPage]
    );

    const visiblePeriods = useMemo(
        () => plan.periods.slice(0, store.monthsOnPage),
        [plan.periods, store.monthsOnPage]
    );

    /**
     * Слоты сетки: день или неделя целиком — к ним липнут жесты. Недели режутся
     * по календарю месяцев, поэтому периоды сюда обязательны.
     */
    const grid = useMemo(
        () => buildGrid(visibleColumns, store.scale, visiblePeriods),
        [visibleColumns, visiblePeriods, store.scale]
    );

    /** Диапазон колонок выделенного слота — подсветка столбца во всех строках */
    const selectedRange = useMemo(() => {
        const slot = selectedSlot === null ? null : grid.slots[selectedSlot];
        return slot && !slot.isEmpty ? { start: slot.start, length: slot.length } : null;
    }, [selectedSlot, grid]);

    /** Месяцы, выровненные по границам слотов: неделя не разрезается пополам */
    const alignedPeriods = useMemo(() => alignPeriods(visiblePeriods, grid), [visiblePeriods, grid]);

    /** Разделители месяцев в строках — в пикселях, ширина дня непостоянна */
    const monthBreaks = useMemo(
        () => alignedPeriods.slice(1).map((period) => period.left),
        [alignedPeriods]
    );

    /** Первый день, который ещё можно менять: всё левее — прошлое */
    const firstEditableColumn = useMemo(() => {
        const index = plan.columns.findIndex((column) => !column.isPast);
        return index < 0 ? plan.columns.length : index;
    }, [plan.columns]);

    const HISTORY_LIMIT = 50;

    /** Правки копятся локально; на сервер уходят по кнопке «Сохранить» */
    const apply = useCallback(
        (next: PlanRow[] | null, refusal: string) => {
            if (!next) {
                store.notify.warning(refusal);
                return;
            }

            setHistory((stack) => [...stack, rows].slice(-HISTORY_LIMIT));
            setRows(next);
        },
        [store, rows]
    );

    const undo = useCallback(() => {
        setHistory((stack) => {
            if (!stack.length) {
                return stack;
            }

            setRows(stack[stack.length - 1]);
            setSelection(null);

            return stack.slice(0, -1);
        });
    }, []);

    const onMove = useCallback(
        (intent: MoveIntent) => {
            if (!canManage) {
                return;
            }

            // в недельном масштабе переносим ячейки: сколько недель взяли,
            // столько и займём — длина недель по дням при этом не важна
            const bySlots = store.scale === "week";
            const slots = bySlots ? slotsOfRange(grid, intent.start, intent.length).length : 0;

            /** Куда встанет отрезок: в недельном масштабе — на целые ячейки */
            const targetRange = bySlots
                ? slotsSpan(grid, intent.target, slots)
                : { start: intent.target, length: intent.length };

            // отсутствие живёт вне очереди работ, поэтому переносится своей операцией:
            // снимаем с прежних дней и ставим на новые, растолкав работы
            const source = rows[intent.fromRow];
            const absence = source ? source.cells[intent.start] : undefined;

            if (isAbsence(absence)) {
                const moved = moveAbsence(
                    rows,
                    plan.columns,
                    absence,
                    intent.copy ? null : { rowIndex: intent.fromRow, start: intent.start, length: intent.length },
                    { rowIndex: intent.toRow, ...targetRange }
                );

                if (moved) {
                    setSelection({ rowIndex: intent.toRow, ...targetRange });
                }

                apply(moved, "Работам некуда сдвинуться — отсутствие поставить нельзя");
                return;
            }

            const next = bySlots
                ? intent.copy
                    ? copySlots(rows, plan.columns, grid, intent)
                    : moveSlots(rows, plan.columns, grid, intent)
                : intent.copy
                ? copyRange(rows, plan.columns, intent)
                : moveRange(rows, plan.columns, intent);

            // оставляем блок подсвеченным на новом месте — видно, что именно переехало
            if (next) {
                setSelection({ rowIndex: intent.toRow, ...targetRange });
            }

            apply(next, "Работы не помещаются до конца периода");
        },
        [apply, rows, plan.columns, canManage, grid, store.scale]
    );

    const onResize = useCallback(
        (intent: ResizeIntent) => {
            if (!canManage) {
                return;
            }

            // отсутствие тянется теми же ручками: снимаем со старых границ, ставим на новые
            const row = rows[intent.rowIndex];
            const absence = row ? row.cells[intent.start] : undefined;

            if (isAbsence(absence)) {
                apply(
                    moveAbsence(
                        rows,
                        plan.columns,
                        absence,
                        { rowIndex: intent.rowIndex, start: intent.start, length: intent.length },
                        { rowIndex: intent.rowIndex, start: intent.newStart, length: intent.newLength }
                    ),
                    "Работам некуда сдвинуться — отсутствие поставить нельзя"
                );
                return;
            }

            // левый край перезаписывает соседа слева, правый — толкает всех вправо
            if (intent.newStart !== intent.start) {
                apply(
                    resizeBlockLeft(
                        rows,
                        plan.columns,
                        intent.rowIndex,
                        intent.start,
                        intent.length,
                        intent.newStart
                    ),
                    "Блок не помещается в период"
                );
                return;
            }

            apply(
                resizeBlockRight(rows, plan.columns, intent.rowIndex, intent.start, intent.length, intent.newLength),
                "Работы не помещаются даже с учётом следующих месяцев"
            );
        },
        [apply, rows, plan.columns, canManage]
    );

    usePlanInteractions({
        containerRef: gridRef,
        scrollRef,
        columnsCount: visibleColumns.length,
        firstEditableColumn,
        grid,
        enabled: canManage && visibleColumns.length > 0,
        onMove,
        onResize,
        onPreview: setDropPreview,
    });

    const onSelectRange = useCallback((next: CellSelection | null) => {
        setSelection(next);
        setSelectedSlot(null);
    }, []);

    useRangeSelection({
        containerRef: gridRef,
        grid,
        enabled: canManage && visibleColumns.length > 0,
        onChange: onSelectRange,
    });

    /** Клик по шапке выделяет столбец (день или неделю) у всех сотрудников; повторный — снимает */
    const onSelectSlot = useCallback(
        (slot: number) => {
            if (grid.slots[slot] && grid.slots[slot].isEmpty) {
                return;
            }

            setSelection(null);
            setSelectedSlot((current) => (current === slot ? null : slot));
        },
        [grid]
    );

    const openPicker = useCallback((target: CellSelection, x: number, y: number) => {
        setSelection(target);
        setPicker({ x, y, selection: target });
    }, []);

    const onBlockClick = useCallback((rowIndex: number, start: number, length: number) => {
        setSelection({ rowIndex, start, length });
    }, []);

    /**
     * Двойной клик по дню открывает выбор проекта:
     *  - если день попал в текущее выделение — заполняем всё выделение;
     *  - если это часть блока — заполняем весь блок;
     *  - иначе — один день.
     */
    const onDayActivate = useCallback(
        (rowIndex: number, column: number, x: number, y: number) => {
            if (!canManage || column < firstEditableColumn) {
                return;
            }

            const inSelection =
                selection &&
                selection.rowIndex === rowIndex &&
                column >= selection.start &&
                column < selection.start + selection.length;

            if (inSelection && selection) {
                openPicker(selection, x, y);
                return;
            }

            const row = rows[rowIndex];
            const block = row ? blockAt(blocksOf(row, visibleColumns), column) : undefined;

            // вне блока заполняем слот целиком: в недельном масштабе — всю неделю
            const slot = slotRange(grid, column);

            openPicker(
                block
                    ? { rowIndex, start: block.start, length: block.length }
                    : { rowIndex, start: Math.max(slot.start, firstEditableColumn), length: slot.length },
                x,
                y
            );
        },
        [canManage, selection, rows, visibleColumns, openPicker, firstEditableColumn, grid]
    );

    const onPick = useCallback(
        (orderId: string) => {
            if (!picker) {
                return;
            }

            const { rowIndex, start, length } = picker.selection;
            setPicker(null);
            apply(fillRange(rows, plan.columns, rowIndex, start, length, orderId), "Нельзя заполнить эти дни");
        },
        [picker, apply, rows, plan.columns]
    );

    /**
     * Отпуск, больничный, БС ставятся тем же окном, что и проект. Отсутствие
     * закреплено за датой: по свободным дням оно просто встаёт на место, а наехав
     * на работы — толкает их вправо, как расширение блока правым краем.
     */
    const onPickAbsence = useCallback(
        (absence: PlanAbsenceOption) => {
            if (!picker) {
                return;
            }

            const { rowIndex, start, length } = picker.selection;
            setPicker(null);
            apply(
                fillAbsence(rows, plan.columns, rowIndex, start, length, absence),
                "Работам некуда сдвинуться — отсутствие поставить нельзя"
            );
        },
        [picker, apply, rows, plan.columns]
    );

    const onClear = useCallback(() => {
        if (!picker) {
            return;
        }

        const { rowIndex, start, length } = picker.selection;
        setPicker(null);
        setSelection(null);
        apply(clearRange(rows, plan.columns, rowIndex, start, length), "Нельзя очистить эти дни");
    }, [picker, apply, rows, plan.columns]);

    /** Скопировать содержимое диапазона в буфер страницы */
    const copySelection = useCallback(
        (target: CellSelection) => {
            const groups = readSlots(rows, plan.columns, grid, target.rowIndex, target.start, target.length);
            const days = readRange(rows, plan.columns, target.rowIndex, target.start, target.length);

            if (days.length) {
                setClipboard({ scale: store.scale, groups, days });
            }
        },
        [rows, plan.columns, grid, store.scale]
    );

    /**
     * Вставить буфер начиная с колонки, перезаписав эти дни.
     * В недельном масштабе вставляются ячейки: сколько недель скопировали,
     * столько и заполнится — даже если рабочих дней в них другое число.
     */
    const pasteAt = useCallback(
        (rowIndex: number, column: number) => {
            if (!clipboard) {
                return;
            }

            const bySlots = store.scale === "week" && clipboard.scale === "week";

            setSelection(
                bySlots
                    ? { rowIndex, ...slotsSpan(grid, column, clipboard.groups.length) }
                    : { rowIndex, start: column, length: clipboard.days.length }
            );

            apply(
                bySlots
                    ? pasteSlots(rows, plan.columns, grid, rowIndex, column, clipboard.groups)
                    : pasteRange(rows, plan.columns, rowIndex, column, clipboard.days),
                "Отрезок не помещается до конца периода"
            );
        },
        [clipboard, apply, rows, plan.columns, grid, store.scale]
    );

    /**
     * Вставить буфер, раздвинув всё, что правее.
     * В недельном масштабе раздвигается на целые ячейки: сколько недель скопировали,
     * столько и появится — иначе неделя закрывалась бы лишь частично.
     */
    const pasteWithShiftAt = useCallback(
        (rowIndex: number, column: number) => {
            if (!clipboard) {
                return;
            }

            const bySlots = store.scale === "week" && clipboard.scale === "week";

            setSelection(
                bySlots
                    ? { rowIndex, ...slotsSpan(grid, column, clipboard.groups.length) }
                    : { rowIndex, start: column, length: clipboard.days.length }
            );

            apply(
                bySlots
                    ? insertSlotsWithShift(rows, plan.columns, grid, rowIndex, column, clipboard.groups)
                    : insertWithShift(rows, plan.columns, rowIndex, column, clipboard.days),
                "Сдвиг вытесняет работы за конец периода"
            );
        },
        [clipboard, apply, rows, plan.columns, grid, store.scale]
    );

    const clearSelection = useCallback(
        (target: CellSelection) => {
            setSelection(null);
            apply(
                clearRange(rows, plan.columns, target.rowIndex, target.start, target.length),
                "Нельзя очистить эти дни"
            );
        },
        [apply, rows, plan.columns]
    );

    /** Убрать дни и подтянуть к ним всё, что стояло правее */
    const deleteShifting = useCallback(
        (target: CellSelection) => {
            setSelection(null);
            apply(
                deleteWithShift(rows, plan.columns, target.rowIndex, target.start, target.length),
                "Нельзя удалить эти дни"
            );
        },
        [apply, rows, plan.columns]
    );

    /** ПКМ: работаем с выделением, если клик попал в него, иначе — с блоком или целым слотом */
    const onDayContext = useCallback(
        (rowIndex: number, column: number, x: number, y: number) => {
            // column < 0 — курсор над неделей без рабочих дней
            if (!canManage || column < 0) {
                return;
            }

            const inSelection =
                selection &&
                selection.rowIndex === rowIndex &&
                column >= selection.start &&
                column < selection.start + selection.length;

            let target: CellSelection;

            if (inSelection && selection) {
                target = selection;
            } else {
                const row = rows[rowIndex];
                const block = row ? blockAt(blocksOf(row, visibleColumns), column) : undefined;

                // вне блока берём слот целиком: в недельном масштабе — всю неделю
                const slot = slotRange(grid, column);

                target = block
                    ? { rowIndex, start: block.start, length: block.length }
                    : { rowIndex, start: slot.start, length: slot.length };

                setSelection(target);
            }

            setPicker(null);
            setContextMenu({ x, y, selection: target });
        },
        [canManage, selection, rows, visibleColumns, grid]
    );

    const contextActions = useMemo<ContextMenuAction[]>(() => {
        if (!contextMenu) {
            return [];
        }

        const target = contextMenu.selection;
        const cells = readRange(rows, plan.columns, target.rowIndex, target.start, target.length);
        // копировать можно только работы, а стирать — и дни отсутствия тоже
        const hasWork = cells.some(isWork);
        const erasable = hasContent(rows, plan.columns, target.rowIndex, target.start, target.length);
        // прошедшие дни менять нельзя, но копировать из них можно
        const readOnly = target.start < firstEditableColumn;

        // в недельном масштабе буфер меряется ячейками — столько же их и появится
        const bySlots = clipboard && store.scale === "week" && clipboard.scale === "week";
        const pasteHint = clipboard
            ? bySlots
                ? `${clipboard.groups.length} нед.`
                : `${clipboard.days.length} дн.`
            : undefined;

        return [
            {
                key: "copy",
                label: texts.copy,
                icon: "general-copy",
                disabled: !hasWork,
                onClick: () => copySelection(target),
            },
            {
                key: "paste",
                label: "Вставить",
                icon: "general-doc-check",
                disabled: !clipboard || readOnly,
                hint: pasteHint,
                onClick: () => pasteAt(target.rowIndex, target.start),
            },
            {
                key: "paste-shift",
                label: "Вставить со сдвигом",
                icon: "general-big-arrow-right",
                disabled: !clipboard || readOnly,
                hint: pasteHint,
                onClick: () => pasteWithShiftAt(target.rowIndex, target.start),
            },
            {
                key: "clear",
                label: "Очистить",
                icon: "general-trash",
                danger: true,
                disabled: !erasable || readOnly,
                onClick: () => clearSelection(target),
            },
            {
                key: "clear-shift",
                label: "Очистить со сдвигом",
                icon: "general-big-arrow-left",
                danger: true,
                disabled: !erasable || readOnly,
                onClick: () => deleteShifting(target),
            },
            { key: "undo-group", label: "", icon: "", separator: true, onClick: () => undefined },
            {
                key: "undo",
                label: "Отменить действие",
                icon: "general-undo",
                disabled: !history.length,
                onClick: undo,
            },
        ];
    }, [
        contextMenu,
        rows,
        plan.columns,
        clipboard,
        store.scale,
        history.length,
        firstEditableColumn,
        copySelection,
        pasteAt,
        pasteWithShiftAt,
        clearSelection,
        deleteShifting,
        undo,
    ]);

    // горячих клавиш всего две: Esc — отменить правки, Enter — назначить проект
    useEffect(() => {
        if (!canManage) {
            return;
        }

        const onKeyDown = (event: KeyboardEvent) => {
            const target = event.target as HTMLElement;
            const inInput = target && (target.tagName === "INPUT" || target.tagName === "TEXTAREA");

            if (event.key === "Escape" && !picker && !contextMenu && dirty) {
                event.preventDefault();
                cancel();
                return;
            }

            if (!selection || picker || contextMenu || inInput) {
                return;
            }

            if (event.key === "Enter") {
                event.preventDefault();

                const element = gridRef.current
                    ? gridRef.current.querySelector(`.wp-selection[data-row="${selection.rowIndex}"]`)
                    : null;
                const rect = element ? element.getBoundingClientRect() : null;

                openPicker(selection, rect ? rect.left : 200, rect ? rect.bottom + 4 : 200);
            }
        };

        document.addEventListener("keydown", onKeyDown);
        return () => document.removeEventListener("keydown", onKeyDown);
    }, [selection, picker, contextMenu, openPicker, canManage, dirty, cancel]);

    // не даём молча уйти со страницы с несохранённым планом
    useEffect(() => {
        if (!dirty) {
            return;
        }

        const onBeforeUnload = (event: BeforeUnloadEvent) => {
            event.preventDefault();
            event.returnValue = "";
        };

        window.addEventListener("beforeunload", onBeforeUnload);
        return () => window.removeEventListener("beforeunload", onBeforeUnload);
    }, [dirty]);

    const departments = useMemo(() => {
        const query = store.pureQuery;
        const groups = new Map<string, DepartmentRow[]>();

        rows.forEach((row, index) => {
            if (query) {
                const haystack = `${row.label} ${row.department} ${row.position}`.toLowerCase();

                if (!haystack.includes(query)) {
                    return;
                }
            }

            const list = groups.get(row.department) || [];
            list.push({ row, index });
            groups.set(row.department, list);
        });

        groups.forEach((list) => list.sort((a, b) => a.row.label.localeCompare(b.row.label)));

        return Array.from(groups.entries());
    }, [rows, store.pureQuery]);

    const visibleCount = useMemo(
        () => departments.reduce((sum, [, list]) => sum + list.length, 0),
        [departments]
    );

    const monthsOptions = MONTHS_OPTIONS[store.scale];

    const monthsOption = useMemo(
        () => monthsOptions.find((item) => item.id === String(store.monthsOnPage)) || monthsOptions[0],
        [monthsOptions, store.monthsOnPage]
    );

    const scaleOption = useMemo(
        () => SCALE_OPTIONS.find((item) => item.id === store.scale) || SCALE_OPTIONS[0],
        [store.scale]
    );

    const pickerCurrentId = useMemo(() => {
        if (!picker) {
            return null;
        }

        const row = rows[picker.selection.rowIndex];

        if (!row) {
            return null;
        }

        const block = blockAt(blocksOf(row, plan.columns), picker.selection.start);
        const cell = row.cells[picker.selection.start];

        if (block) {
            return block.absence ? null : block.orderId;
        }

        return isWork(cell) ? cell.orderId : null;
    }, [picker, rows, plan.columns]);

    const pickerCurrentAbsence = useMemo(() => {
        if (!picker) {
            return null;
        }

        const row = rows[picker.selection.rowIndex];
        const cell = row ? row.cells[picker.selection.start] : undefined;

        return isAbsence(cell) ? cell.type : null;
    }, [picker, rows]);

    const absences = useMemo(() => planAbsenceOptions(), []);

    return (
        <div className={styles.page}>
            <LocalLoader active={store.isLoading || !loaded} />
            {store.isSaving && <SavingOverlay />}

            <div className={styles.toolbar}>
                <div className={styles.view}>
                    <div className={styles.buttons}>
                        {canReconcile && (
                            <PlanrButton
                                type="secondary"
                                size="small"
                                onClick={() => setReconcileOpen(true)}
                                title="Перенести невыполненные работы вправо по данным табеля"
                            >
                                Сверить с табелем
                            </PlanrButton>
                        )}

                        <PlanrButton
                            type="lightblueish"
                            size="small"
                            style={HELP_BUTTON_STYLE}
                            onClick={() => setHelpOpen(true)}
                        >
                            Инструкция по работе
                        </PlanrButton>
                    </div>

                    <div className={styles.selects}>
                        <div className={styles.scale} title="Масштаб сетки: дни или недели">
                            <SimpleSelect
                                className={`full-width-select ${Classes.FILL}`}
                                filterable={false}
                                activeItem={scaleOption}
                                items={SCALE_OPTIONS}
                                itemRenderer={renderSingleOption}
                                onItemSelect={(item: SelectItem) => changeScale(item.id as PlanScale)}
                            >
                                <DefaultSelectedOption option={scaleOption} />
                            </SimpleSelect>
                        </div>

                        <div className={styles.months} title="Сколько месяцев показывать в этом масштабе">
                            <SimpleSelect
                                className={`full-width-select ${Classes.FILL}`}
                                filterable={false}
                                activeItem={monthsOption}
                                items={monthsOptions}
                                itemRenderer={renderSingleOption}
                                onItemSelect={(item: SelectItem) => changeMonthsOnPage(+item.id)}
                            >
                                <DefaultSelectedOption option={monthsOption} />
                            </SimpleSelect>
                        </div>
                    </div>
                </div>

                <div className={styles.period}>
                    <PeriodRangeSlider
                        year={store.year}
                        month={store.month}
                        monthsCount={store.monthsOnPage}
                        onChange={gotoMonth}
                    />

                    <div className={styles.norms}>
                        <span className={styles.norm}>
                            <Peoples count={visibleCount} />
                        </span>
                    </div>
                </div>

                <div className={styles.actions}>
                    <Search query={store.query} onSearch={setUserFilter} />
                    <Zoomer zoom={store.zoom} min={50} max={100} step={5} onChange={store.setZoom} />
                    <CollapseExpandAll onChange={store.collapser.setAll} />
                </div>

            </div>

            <div className={styles.scroll} ref={scrollRef}>
                {/*
                    Зум сетки — через CSS zoom, а не transform: scale. Transform заводит
                    свой контекст координат: липкая шапка дней внутри него перестаёт липнуть
                    на любом масштабе, кроме 100%, и уезжает вверх вместе со строками.
                    zoom масштабирует саму раскладку, поэтому sticky работает как обычно,
                    а ширина прокрутки совпадает с видимой сеткой (при scale контейнер
                    оставался прежней ширины, и справа висела пустота). Жесты не страдают:
                    все замеры идут через getBoundingClientRect (trackScale / columnAt).
                */}
                <div className={styles.grid} ref={gridRef} style={{ zoom: store.transform }}>
                    <PlanDayHeader
                        periods={alignedPeriods}
                        grid={grid}
                        selectedSlot={selectedSlot}
                        onSelectSlot={onSelectSlot}
                    />

                    {departments.map(([department, list]) => (
                        <DepartmentSection
                            key={department}
                            department={department}
                            rows={list}
                            columns={visibleColumns}
                            projects={projectsMap}
                            selection={selection}
                            dropPreview={dropPreview}
                            grid={grid}
                            selectedSlot={selectedRange}
                            monthBreaks={monthBreaks}
                            collapsed={!store.collapser.opened.get(department)}
                            onToggle={store.collapser.toggle}
                            onBlockClick={onBlockClick}
                            onDayActivate={onDayActivate}
                            onDayContext={onDayContext}
                        />
                    ))}
                </div>
            </div>

            <PlanrTooltip containerRef={gridRef} />

            {loaded && !store.isLoading && !visibleCount && <NoData className={styles.nodata} />}

            <Prompt when={dirty} message="План изменён, но не сохранён. Уйти со страницы и потерять правки?" />

            {dirty && (
                <div className={styles.submitBar}>
                    <PlanSubmitButtons
                        disabled={store.isSaving}
                        canUndo={history.length > 0}
                        onSubmit={save}
                        onCancel={cancel}
                        onUndo={undo}
                    />
                </div>
            )}

            {contextMenu && (
                <ContextMenu
                    x={contextMenu.x}
                    y={contextMenu.y}
                    actions={contextActions}
                    onClose={() => setContextMenu(null)}
                />
            )}

            <PlanHelpDialog isOpen={helpOpen} onClose={() => setHelpOpen(false)} />

            <PlanReconcileDialog
                isOpen={reconcileOpen}
                busy={store.isSaving}
                onConfirm={reconcile}
                onClose={() => setReconcileOpen(false)}
            />

            {picker && (
                <ProjectPicker
                    x={picker.x}
                    y={picker.y}
                    projects={plan.projects}
                    absences={absences}
                    currentId={pickerCurrentId}
                    currentAbsence={pickerCurrentAbsence}
                    onPick={onPick}
                    onPickAbsence={onPickAbsence}
                    onClear={onClear}
                    onClose={() => setPicker(null)}
                />
            )}
        </div>
    );
});

export type { PlanColumn };
